import { redactUrl } from './redaction';

export type RuntimeInfo = {
  documentReadyState: DocumentReadyState;
  visibilityState: DocumentVisibilityState;
  windowFocused: boolean;
  pageLifetimeMs?: number;
  navigationType?: string;
  domContentLoadedMs?: number;
  loadEventMs?: number;
  serviceWorker?: string;
  scripts: string[];
};

export type ResourceTimingInfo = {
  timestamp?: string;
  initiatorType: string;
  url: string;
  durationMs: number;
  responseStatus?: number;
  transferSize?: number;
};

function roundedPositive(value: number | undefined): number | undefined {
  return value === undefined || !Number.isFinite(value) || value <= 0 ? undefined : Math.round(value);
}

function collectNavigationTiming(): PerformanceNavigationTiming | undefined {
  try {
    return performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  } catch {
    return undefined;
  }
}

function collectScripts(): string[] {
  try {
    return [...document.scripts]
      .filter((script) => script.src !== '')
      .map((script) => {
        const type = script.type === 'module' ? 'module' : script.type || 'classic';
        return `${redactUrl(script.src)} (${type})`;
      });
  } catch {
    return [];
  }
}

function describeServiceWorker(): string | undefined {
  try {
    const controller = navigator.serviceWorker?.controller;
    return controller === null || controller === undefined
      ? undefined
      : `${redactUrl(controller.scriptURL)} (${controller.state})`;
  } catch {
    return undefined;
  }
}

export function collectRuntimeInfo(): RuntimeInfo {
  const navigation = collectNavigationTiming();

  return {
    documentReadyState: document.readyState,
    visibilityState: document.visibilityState,
    windowFocused: document.hasFocus(),
    pageLifetimeMs: roundedPositive(performance.now()),
    navigationType: navigation?.type,
    domContentLoadedMs: roundedPositive(navigation?.domContentLoadedEventEnd),
    loadEventMs: roundedPositive(navigation?.loadEventEnd),
    serviceWorker: describeServiceWorker(),
    scripts: collectScripts(),
  };
}

function resourceTimestamp(entry: PerformanceResourceTiming): string | undefined {
  const timeOrigin = performance.timeOrigin;
  if (!Number.isFinite(timeOrigin) || !Number.isFinite(entry.startTime)) {
    return undefined;
  }

  try {
    return new Date(timeOrigin + entry.startTime).toISOString();
  } catch {
    return undefined;
  }
}

function redactResourceUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    for (const key of [...url.searchParams.keys()]) {
      url.searchParams.set(key, '[redacted]');
    }
    url.hash = '';
    return redactUrl(url.toString());
  } catch {
    return redactUrl(rawUrl);
  }
}

export function collectRecentResourceTimings(limit = 25): {
  entries: ResourceTimingInfo[];
  dropped: number;
} {
  try {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const selected = resources.slice(-limit);

    return {
      entries: selected.map((entry) => {
        const extendedEntry = entry as PerformanceResourceTiming & { responseStatus?: number };
        return {
          timestamp: resourceTimestamp(entry),
          initiatorType: entry.initiatorType || 'resource',
          url: redactResourceUrl(entry.name),
          durationMs: Math.round(entry.duration),
          responseStatus: roundedPositive(extendedEntry.responseStatus),
          transferSize: entry.transferSize === 0 ? undefined : roundedPositive(entry.transferSize),
        };
      }),
      dropped: resources.length - selected.length,
    };
  } catch {
    return { entries: [], dropped: 0 };
  }
}
